import { ActionFunction, LoaderFunctionArgs, redirect } from "react-router";
import { runGqlMutation, runGqlQuery } from "gqlClient";
import {
  CreatePageLabelDocument,
  CreatePageLabelRuleDocument,
  CreatePageLabelRuleMutationVariables,
  DeletePageLabelDocument,
  DeletePageLabelRuleDocument,
  GetPageLabelsDocument,
  PageLabel,
  PageLabelListing,
  PageLabelRule,
  UpdatePageLabelRuleDocument,
  UpdatePageLabelRuleMutationVariables,
} from "generated/graphql";
import { LoaderFunction, useRouteLoaderData } from "react-router-typesafe";
import { ClipboardPageLabel } from "routes/lens-editor/page-labels/ClipboardPageLabel";
import { array, boolean, nullable, number, object, optional, parse, picklist, string } from "valibot";
import store from "redux/store";
import { addLoaderError } from "redux/slices/loader-error.slice";
import { parseSafeInt } from "Backend";

export const pageLabelsLoader = ((args: LoaderFunctionArgs) => {
  const lensId = parseSafeInt(args.params.lensId);

  if (lensId === undefined) {
    return redirect("..");
  }

  return runGqlQuery(GetPageLabelsDocument, {
    lensId,
  })
    .then((r) => r.lens.pageLabels.listPageLabels)
    .catch((e) => {
      console.error(e);
      store.dispatch(addLoaderError({ loaderName: "pageLabelsLoader", message: "Unable to load page labels" }));
      return [];
    });
}) satisfies LoaderFunction;

export const usePageLabelsLoader = () => {
  return useRouteLoaderData<typeof pageLabelsLoader>("lens-editor/lens/page-labels");
};

export const usePageLabelIdLoader: (pageLabelId: number) => PageLabelListing | undefined = (pageLabelId) => {
  const pageLabels = usePageLabelsLoader();
  return pageLabels.find((pl) => pl.label.id === pageLabelId);
};

export const makePageLabelForCopy: (label: PageLabel, rules: PageLabelRule[]) => ClipboardPageLabel = (
  label,
  rules
) => {
  return {
    label: {
      name: label.name,
      preventsLensHits: label.preventsLensHits,
      triggersFormExtraction: label.triggersFormExtraction,
      triggersTableExtraction: label.triggersTableExtraction,
    },
    rules: rules.map((r) => ({
      groupName: r.groupName,
      queryFuzziness: r.queryFuzziness,
      queryPhraseSlop: r.queryPhraseSlop,
      queryText: r.queryText,
      exclude: r.exclude,
    })),
  };
};

const PageLabelRuleActionSchema = object({
  intent: picklist(["create", "delete", "update"]),
  id: optional(number()),
  pageLabelId: number(),
  groupName: optional(string()),
  queryText: string(),
  queryPhraseSlop: optional(number()),
  queryFuzziness: optional(string()),
  exclude: optional(boolean()),
});

interface FetcherError {
  error: any;
}

const createRule = async (
  newPageLabelRule: CreatePageLabelRuleMutationVariables
): Promise<PageLabelRule | FetcherError> => {
  try {
    const r = await runGqlMutation(CreatePageLabelRuleDocument, newPageLabelRule);
    return r.lens.pageLabels.upsertPageLabelRule;
  } catch (e) {
    return { error: e };
  }
};

const updateRule = async (
  pageLabelRule: UpdatePageLabelRuleMutationVariables
): Promise<PageLabelRule | FetcherError> => {
  try {
    const r = await runGqlMutation(UpdatePageLabelRuleDocument, pageLabelRule);
    return r.lens.pageLabels.upsertPageLabelRule;
  } catch (e) {
    return { error: e };
  }
};

const deleteRule = async (pageLabelRuleId: number): Promise<{ pageLabelRuleId: number } | FetcherError> => {
  try {
    await runGqlMutation(DeletePageLabelRuleDocument, { id: pageLabelRuleId });
    return { pageLabelRuleId };
  } catch (e) {
    return { error: e };
  }
};

export const pageLabelRuleAction: ActionFunction = async ({ request }) => {
  const pageLabelRuleRequest = await request.json();
  const action = parse(PageLabelRuleActionSchema, pageLabelRuleRequest);
  const upsertRule = {
    pageLabelId: action.pageLabelId,
    groupName: action.groupName,
    queryText: action.queryText,
    queryFuzziness: action.queryFuzziness,
    queryPhraseSlop: action.queryPhraseSlop,
    exclude: action.exclude ?? false,
  };

  if (action.intent === "create") {
    return createRule(upsertRule);
  } else if (action.intent === "update" && action.id !== undefined) {
    return updateRule({
      id: action.id,
      ...upsertRule,
    });
  } else if (action.intent === "delete" && action.id !== undefined) {
    return deleteRule(action.id);
  }
};

const PageLabelsImportActionSchema = array(
  object({
    name: string(),
    preventsLensHits: boolean(),
    triggersFormExtraction: boolean(),
    triggersTableExtraction: boolean(),
    rules: array(
      object({
        groupName: optional(nullable(string())),
        queryText: string(),
        queryPhraseSlop: optional(nullable(number())),
        queryFuzziness: optional(nullable(string())),
        exclude: boolean(),
      })
    ),
  })
);
export const pageLabelsImportAction: ActionFunction = async ({ request, params }) => {
  const pageLabelsImportRequest = await request.json();
  const paramsLensId = params.lensId;

  if (paramsLensId) {
    const action = parse(PageLabelsImportActionSchema, pageLabelsImportRequest);
    const promises = action.map((pl) =>
      runGqlMutation(CreatePageLabelDocument, {
        lensId: parseInt(paramsLensId),
        ...pl,
      }).then((r) => {
        const pageLabelId = r.lens.pageLabels.createPageLabel.id;
        const rulePromises = pl.rules.map((rule) =>
          runGqlMutation(CreatePageLabelRuleDocument, {
            pageLabelId,
            ...rule,
          })
        );
        return Promise.all(rulePromises);
      })
    );
    return Promise.all(promises).catch((e) => ({ error: e }));
  }
  return null;
};

const PageLabelsActionSchema = object({
  intent: picklist(["create", "delete"]),
  pageLabelId: optional(number()),
  newPageLabel: optional(
    object({
      name: string(),
      preventsLensHits: boolean(),
      triggersFormExtraction: boolean(),
      triggersTableExtraction: boolean(),
    })
  ),
});
export const pageLabelsAction: ActionFunction = async ({ request, params }) => {
  const pageLabelsRequest = await request.json();
  const action = parse(PageLabelsActionSchema, pageLabelsRequest);
  const paramsLensId = params.lensId;

  if (action.intent === "delete" && action.pageLabelId) {
    await runGqlMutation(DeletePageLabelDocument, { id: action.pageLabelId });
    return redirect(`/lens-editor/${paramsLensId}/page-labels/list`);
  } else if (action.intent === "create" && action.newPageLabel && paramsLensId) {
    return runGqlMutation(CreatePageLabelDocument, {
      lensId: parseInt(paramsLensId),
      ...action.newPageLabel,
    }).then((r) => r.lens.pageLabels.createPageLabel);
  }
};
